
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./components/layout/Sidebar";
import { LoadingProvider } from "./hooks/useLoading";
import Index from "./pages/Index";
import Attendance from "./pages/attendance/Attendance";
import Schedules from "./pages/schedules/Schedules";
import Classes from "./pages/classes/Classes";
import Batches from "./pages/batches/AllBatches";
import AddOrEditBatch from "./pages/batches/AddOrEditBatch";
import Subjects from "./pages/subjects/Subjects";
import Manipulate from "./pages/manipulate/Manipulate";
import Punch from "./pages/punch/Punch";
import Lms from "./pages/lms/Lms";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import { useToast } from './hooks/use-toast';
import './App.css';
import Courses from "./pages/Courses";
import CourseDetails from "./pages/CourseDetails";
import EditCourse from "./pages/EditCourse";
import AddEditModule from "./pages/modules/AddEditModule";
import { useEffect, useState } from "react";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from "./firebase";
import Login from "./pages/Login";
import Manipulation from "./pages/Manipulation";
import { onSnapshot } from 'firebase/firestore';
const queryClient = new QueryClient();


// PrivateRoute component to handle protected routes  
const PrivateRoute = ({ element, isLoggedIn }) => {
  return isLoggedIn ? element : <Navigate to="/login" />;
};

const App = () => {

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const db = getFirestore(app);
  const auth = getAuth(app);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();



  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user')) ?? null;
    const checkUserLoggedIn = async () => {

      if (userData && userData?.uid && userData.tokenCreatedAt) {
        try {
          const uid = userData.uid;
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where("uid", "==", uid));
          const querySnapshot = await getDocs(q);

          const studentDoc = querySnapshot.docs[0];
          const studentDetails: any = { id: studentDoc.id, ...studentDoc.data() };
          if (studentDetails?.role !== "MASTERADMIN" && studentDetails?.role !== "INSTRUCTOR" && studentDetails?.role !== "ADMIN" && studentDetails?.role !== "SUPERADMIN") {
            setTimeout(() => {
              toast({
                title: "Access Denied",
                description: "Only Admins have access to ByteSplash Information Portal.",
              });
            }, 2000)
            localStorage.clear();
            localStorage.setItem('errors', JSON.stringify(({
              title: "Access Denied",
              description: "Only Admins have access to ByteSplash Information Portal.",
            })));
            window.location.href = "/login";
          }
          if (!querySnapshot.empty) {
            setIsLoggedIn(true);
            setIsLoading(false);
            navigate(location.pathname);
          } else {
            setIsLoading(false);
            console.error("No user record found in Firestore");
          }
        } catch (error) {
          setIsLoading(false);
          console.error("Error during authentication:", error);
        }
      } else {
        localStorage.clear();
         navigate("/login");  
      }

      setIsLoading(false);
    };


    const usersUnsubscribe = onSnapshot(
      collection(db, "users"),
      (querySnapshot) => {
        const users = querySnapshot.docs.map(doc => doc.data());
        checkUserLoggedIn();
      },
      (error) => {
      }
    );

    checkUserLoggedIn();

    return () => {
      usersUnsubscribe();
    };

  }, [db, auth]);

  // Redirect if not logged in and trying to access protected routes  
  useEffect(() => {
    if (!isLoading &&
      !isLoggedIn && location.pathname !== '/login'
      && location.pathname !== '/students/draft-register') {
      navigate("/login");
    }
  }, [isLoggedIn, isLoading, navigate]);

  if (isLoading) {
    return <div>Loading...</div>; // You can replace this with a loading spinner or similar component  
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LoadingProvider>
          <Toaster />
          <Sonner />
          <div className="flex h-auto w-full">
            <Sidebar isLoggedIn={isLoggedIn} />
            <main className="flex-1 flex flex-col">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/attandance" element={<PrivateRoute element={<Attendance />} isLoggedIn={isLoggedIn} />} />
                <Route path="/" element={<PrivateRoute element={<Attendance />} isLoggedIn={isLoggedIn} />} />
                <Route path="/dashboard" element={<PrivateRoute element={<Index />} isLoggedIn={isLoggedIn} />} />
                <Route path="/courses" element={<PrivateRoute element={<Courses />} isLoggedIn={isLoggedIn} />} />
                <Route path="/courses/edit/:courseId" element={<PrivateRoute element={<EditCourse />} isLoggedIn={isLoggedIn} />} />
                <Route path="/modules/edit/:moduleId" element={<PrivateRoute element={<AddEditModule />} isLoggedIn={isLoggedIn} />} />
                <Route path="/courses/:courseId" element={<PrivateRoute element={<CourseDetails />} isLoggedIn={isLoggedIn} />} />
                <Route path="/attendance" element={<PrivateRoute element={<Attendance />} isLoggedIn={isLoggedIn} />} />
                <Route path="/schedules" element={<PrivateRoute element={<Schedules />} isLoggedIn={isLoggedIn} />} />
                <Route path="/classes" element={<PrivateRoute element={<Classes />} isLoggedIn={isLoggedIn} />} />
                <Route path="/batches" element={<PrivateRoute element={<Batches />} isLoggedIn={isLoggedIn} />} />
                <Route path="/batches/create" element={<PrivateRoute element={<AddOrEditBatch />} isLoggedIn={isLoggedIn} />} />
                <Route path="/batches/edit/:batchId" element={<PrivateRoute element={<AddOrEditBatch />} isLoggedIn={isLoggedIn} />} />
                <Route path="/subjects" element={<PrivateRoute element={<Subjects />} isLoggedIn={isLoggedIn} />} />
                <Route path="/manipulate" element={<PrivateRoute element={<Manipulate />} isLoggedIn={isLoggedIn} />} />
                <Route path="/punch" element={<PrivateRoute element={<Punch />} isLoggedIn={isLoggedIn} />} />
                <Route path="/lms" element={<PrivateRoute element={<Courses />} isLoggedIn={isLoggedIn} />} />
                <Route path="/settings" element={<PrivateRoute element={<Settings />} isLoggedIn={isLoggedIn} />} />
                <Route path="/profile" element={<PrivateRoute element={<Profile />} isLoggedIn={isLoggedIn} />} />
                {/* Manipulation route */}
                <Route path="/manipulation" element={<PrivateRoute element={<Manipulation />} isLoggedIn={isLoggedIn} />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
          </div>
        </LoadingProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
